import {
  getAllVehiclesQueryKey,
  getCurrentDrivesQueryKey,
  updateVehicleMutation,
} from '@/api/main/@tanstack/react-query.gen'
import { Button, Modal, Stack, Textarea, TextInput } from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

const VehicleCreateForm = ({ opened, onClose }: { opened: boolean; onClose: () => void }) => {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['vehicles', 'common'])

  const { mutate: updateVehicle, isPending } = useMutation({
    ...updateVehicleMutation(),
    onSuccess: () => {
      notifications.show({ message: t('vehicleUpdated') })
      queryClient.invalidateQueries({ queryKey: [getCurrentDrivesQueryKey(), getAllVehiclesQueryKey()] })
      handleClose()
    },
    onError: () => {
      notifications.show({ message: t('errorUpdatingVehicle'), color: 'red' })
    },
  })

  const form = useForm({
    mode: 'uncontrolled',
    initialValues: {
      vehicleId: '',
      password: '',
      phoneNumber: '',
      routerPhoneNumber: '',
      vpnAddress: '',
      modemVpnAddress: '',
      controlUnitVpnAddress: '',
      note: '',
    },
    validate: {
      vehicleId: (value: string) => (value && value.trim() !== '' ? null : t('vehicleIdRequired')),
      password: (value: string) => (value && value.trim() !== '' ? null : t('passwordRequired')),
    },
  })

  const handleClose = () => {
    form.reset()
    onClose()
  }

  useEffect(() => {
    if (opened) {
      form.reset()
    }
    // form is a stable Mantine form ref
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened])

  return (
    <Modal opened={opened} onClose={handleClose} size="lg" title={t('createVehicle')}>
      <form
        onSubmit={form.onSubmit(values => {
          updateVehicle({
            body: {
              control_unit_vpn_address:
                values.controlUnitVpnAddress.trim() === '' ? undefined : values.controlUnitVpnAddress,
              phone_number: values.phoneNumber.trim() === '' ? undefined : values.phoneNumber,
              router_phone_number: values.routerPhoneNumber.trim() === '' ? undefined : values.routerPhoneNumber,
              vpn_address: values.vpnAddress.trim() === '' ? undefined : values.vpnAddress,
              modem_vpn_address: values.modemVpnAddress.trim() === '' ? undefined : values.modemVpnAddress,
              password: values.password,
              note: values.note.trim() === '' ? undefined : values.note,
            },
            path: {
              vehicle_id: values.vehicleId,
            },
          })
        })}
      >
        <Stack>
          <TextInput key={form.key('vehicleId')} label={t('id')} required {...form.getInputProps('vehicleId')} />
          <TextInput
            key={form.key('password')}
            label={t('password')}
            {...form.getInputProps('password')}
            type="password"
            required
          />
          <TextInput key={form.key('phoneNumber')} label={t('phoneNumber')} {...form.getInputProps('phoneNumber')} />
          <TextInput
            key={form.key('routerPhoneNumber')}
            label={t('routerPhoneNumber')}
            {...form.getInputProps('routerPhoneNumber')}
          />
          <TextInput key={form.key('vpnAddress')} label={t('carVpnIp')} {...form.getInputProps('vpnAddress')} />
          <TextInput
            key={form.key('modemVpnAddress')}
            label={t('modemVpnIp')}
            {...form.getInputProps('modemVpnAddress')}
          />
          <TextInput
            key={form.key('controlUnitVpnAddress')}
            label={t('controlUnitVpnIp')}
            {...form.getInputProps('controlUnitVpnAddress')}
          />
          <Textarea key={form.key('note')} label={t('note')} {...form.getInputProps('note')} />
          <Button type="submit" styles={{ root: { alignSelf: 'start' } }} loading={isPending}>
            {t('save', { ns: 'common' })}
          </Button>
        </Stack>
      </form>
    </Modal>
  )
}

export default VehicleCreateForm
